import React from "react";
import { Button, Form, FormGroup, Input, Label, Alert, Spinner } from "reactstrap";
import axios from "axios";
import { API_URL_sedes } from "../../constants";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-datepicker/dist/react-datepicker.css";
import { getAuthHeaders } from "../../utils/auth";

class NewSedeForm extends React.Component {
  state = {
    pk: null,
    sede: "",
    sede_code: "",       // keep in state but hidden
    errorMessage: "",
    validationErrors: {},
    loading: false,
  };

  componentDidMount() {
    if (this.props?.sede) {
      const { pk, id, sede, sede_code } = this.props.sede || {};
      this.setState({
        pk: (pk ?? id) ?? null,
        sede: sede ?? "",
        sede_code: sede_code ?? "",   // keep code from backend
      });
    }
  }

  onChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value, errorMessage: "" });
  };

  defaultIfEmpty = (v) => (v ?? "");

  baseUrl = () => {
    const base = (API_URL_sedes || "").trim();
    return base.endsWith("/") ? base : `${base}/`;
  };

  showBackendError = (error, fallback) => {
    let msg = fallback;
    const d = error?.response?.data;
    if (d) {
      if (typeof d === "string") msg = d;
      else if (d.detail) msg = d.detail;
      else {
        msg = Object.entries(d)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
          .join(" | ");
      }
    }
    const allow = error?.response?.headers?.allow;
    if (allow) msg += ` (Allow: ${allow})`;
    this.setState({ errorMessage: msg });
  };

  createSede = async (e) => {
    e.preventDefault();
    const sede = (this.state.sede || "").trim();

    if (!sede) {
      this.setState({
        errorMessage: "Por favor completa todos los campos obligatorios marcados con *",
        validationErrors: { sede: "Por favor ingresa el nombre del Value Stream" }
      });
      setTimeout(() => {
        const firstError = document.querySelector('.is-invalid');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstError.focus();
        }
      }, 100);
      return;
    }

    this.setState({ loading: true, errorMessage: "", validationErrors: {} });
    try {
      await axios.post(this.baseUrl(), { sede }, { headers: getAuthHeaders() });
      this.props.resetState?.();
      this.props.toggle?.();
    } catch (error) {
      if (error.response?.data) {
        const serverErrors = error.response.data;
        if (typeof serverErrors === 'object' && !Array.isArray(serverErrors)) {
          this.setState({ validationErrors: serverErrors, loading: false });
        } else {
          this.showBackendError(error, "Error al crear la sede.");
        }
      } else {
        this.setState({ errorMessage: 'Error de conexión. Por favor intenta nuevamente.', loading: false });
      }
    } finally {
      this.setState({ loading: false });
    }
  };

  editSede = async (e) => {
    e.preventDefault();
    const sede = (this.state.sede || "").trim();
    const sede_code = (this.state.sede_code || "").trim();
    const pk = this.state.pk;

    if (!sede) {
      this.setState({
        errorMessage: "Por favor completa todos los campos obligatorios marcados con *",
        validationErrors: { sede: "Por favor ingresa el nombre del Value Stream" }
      });
      setTimeout(() => {
        const firstError = document.querySelector('.is-invalid');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstError.focus();
        }
      }, 100);
      return;
    }

    if (pk == null || !Number.isFinite(Number(pk))) {
      return this.setState({ errorMessage: "No se encontró el ID de la sede." });
    }

    const url = `${this.baseUrl()}${pk}/`;
    this.setState({ loading: true, errorMessage: "", validationErrors: {} });

    try {
      // PUT with both fields so backend validation passes
      await axios.put(url, { sede, sede_code }, { headers: getAuthHeaders() });
      this.props.resetState?.();
      this.props.toggle?.();
    } catch (error) {
      if (error?.response?.status === 405) {
        try {
          await axios.patch(url, { sede, sede_code }, { headers: getAuthHeaders() });
          this.props.resetState?.();
          this.props.toggle?.();
        } catch (patchErr) {
          if (patchErr.response?.data) {
            const serverErrors = patchErr.response.data;
            if (typeof serverErrors === 'object' && !Array.isArray(serverErrors)) {
              this.setState({ validationErrors: serverErrors, loading: false });
            } else {
              this.showBackendError(patchErr, "Error al actualizar la sede (PATCH).");
            }
          } else {
            this.setState({ errorMessage: 'Error de conexión. Por favor intenta nuevamente.', loading: false });
          }
        }
      } else {
        if (error.response?.data) {
          const serverErrors = error.response.data;
          if (typeof serverErrors === 'object' && !Array.isArray(serverErrors)) {
            this.setState({ validationErrors: serverErrors, loading: false });
          } else {
            this.showBackendError(error, "Error al actualizar la sede (PUT).");
          }
        } else {
          this.setState({ errorMessage: 'Error de conexión. Por favor intenta nuevamente.', loading: false });
        }
      }
    } finally {
      this.setState({ loading: false });
    }
  };

  render() {
    const { errorMessage, loading, sede, validationErrors } = this.state;
    const isEditing = !!this.props.sede;

    return (
      <Form onSubmit={isEditing ? this.editSede : this.createSede}>
        <p style={{ fontSize: '0.9em', color: '#007bff', marginBottom: '1rem' }}>
          Los campos marcados con <span style={{ color: 'red' }}>*</span> son obligatorios
        </p>

        <FormGroup>
          <Label htmlFor="sede">Value Stream: <span style={{ color: 'red' }}>*</span></Label>
          <Input
            id="sede"
            type="text"
            name="sede"
            onChange={this.onChange}
            value={this.defaultIfEmpty(sede)}
            placeholder="Ej. Servicios"
            autoFocus
            invalid={!!validationErrors.sede}
          />
          {validationErrors.sede && (
            <div className="invalid-feedback d-block">{validationErrors.sede}</div>
          )}
        </FormGroup>

        {errorMessage && (
          <Alert color="danger" className="py-2">
            {errorMessage}
          </Alert>
        )}

        <Button color="primary" disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" /> Guardando...
            </>
          ) : (
            isEditing ? "Actualizar Value Stream" : "Crear Value Stream"
          )}
        </Button>
      </Form>
    );
  }
}

export default NewSedeForm;